import { useState } from 'react';
import axios from 'axios';
import { Send, X, Users } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('futurepath_token')}` } });

const TEMPLATES = [
  { id: 'assessment', title: 'Finish your assessment',
    message: 'You are two steps from a real plan, finish your assessment this week and I will build your pathway options with you.',
    cta: '/app/assessment' },
  { id: 'direction', title: 'Let\u2019s pick your direction',
    message: 'You have not chosen a postsecondary path yet. Book time with me and we will narrow it to two options you actually like.',
    cta: '/app/careers' },
  { id: 'wbl', title: 'Log your work hours',
    message: 'Your work-based learning hours are behind. Log what you have already done, most of you are further along than the record shows.',
    cta: '/app/nc-wbl' },
  { id: 'exam', title: 'Register for your exam',
    message: 'ACT and WorkKeys registration closes soon. Register now so your score arrives before applications.',
    cta: '/app/exam-dates' },
];

export function NudgeComposer({ studentIds, onClose, onSent }) {
  const [title, setTitle]     = useState('A note from your counselor');
  const [message, setMessage] = useState('');
  const [cta, setCta]         = useState('/app/dashboard');
  const [recipients, setRecipients] = useState('students');
  const [sending, setSending] = useState(false);
  const [error, setError]     = useState('');

  const send = async () => {
    if (!message.trim()) return;
    setSending(true); setError('');
    try {
      const r = await axios.post(`${API}/counselor/nudge`,
        { student_ids: studentIds, title, message, cta_path: cta, recipients }, auth());
      onSent(r.data);
      onClose();
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not send');
    } finally { setSending(false); }
  };

  const RECIPIENT_OPTS = [
    { id: 'students', label: 'Students' },
    { id: 'parents',  label: 'Parents' },
    { id: 'both',     label: 'Both' },
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0a1729]/70 p-4 backdrop-blur-sm"
      data-testid="nudge-composer">
      <div className="w-full max-w-xl rounded-3xl border border-sky-300/25 p-7"
        style={{ background: 'linear-gradient(160deg,#0b1f38,#0a1729)' }}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="font-outfit text-xl font-semibold text-white">Send a message</h3>
            <p className="mt-1 flex items-center gap-1.5 text-xs text-slate-300">
              <Users className="h-3.5 w-3.5" /> {studentIds.length} student{studentIds.length===1?'':'s'} selected · direct to their FuturePath feed
            </p>
          </div>
          <button onClick={onClose} data-testid="nudge-close"
            className="rounded-lg p-1.5 text-slate-300 hover:bg-white/10 hover:text-white">
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Recipient toggle */}
        <div className="mt-5">
          <p className="text-[10.5px] font-semibold uppercase tracking-[0.16em] text-slate-300 mb-2">Send to</p>
          <div className="flex gap-2" data-testid="nudge-recipients-toggle">
            {RECIPIENT_OPTS.map(o => (
              <button key={o.id} onClick={() => setRecipients(o.id)}
                data-testid={`nudge-recipients-${o.id}`}
                className={`rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors ${
                  recipients === o.id
                    ? 'border-sky-300/60 bg-sky-400/[0.18] text-sky-100'
                    : 'border-white/10 bg-white/[0.04] text-slate-300 hover:bg-white/[0.08]'
                }`}>
                {o.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-2">
          {TEMPLATES.map(t => (
            <button key={t.id} data-testid={`nudge-template-${t.id}`}
              onClick={() => { setTitle(t.title); setMessage(t.message); setCta(t.cta); }}
              className="rounded-full border border-white/12 bg-white/[0.05] px-3 py-1.5 text-[11.5px]
                text-slate-300 hover:border-sky-300/50 hover:text-white transition-colors">
              {t.title}
            </button>
          ))}
        </div>

        <input value={title} onChange={e => setTitle(e.target.value)} data-testid="nudge-title"
          className="mt-4 w-full rounded-xl border border-white/12 bg-white/[0.04] px-4 py-3 text-sm text-white
            placeholder:text-slate-300 focus:border-sky-300/50 focus:outline-none" placeholder="Subject" />
        <textarea value={message} onChange={e => setMessage(e.target.value)} rows={5} data-testid="nudge-message"
          className="mt-3 w-full resize-none rounded-xl border border-white/12 bg-white/[0.04] px-4 py-3 text-sm
            text-white placeholder:text-slate-300 focus:border-sky-300/50 focus:outline-none"
          placeholder="What do they need to do, and by when?" />

        {error && <p className="mt-3 text-xs font-semibold text-rose-400" data-testid="nudge-error">{error}</p>}

        <div className="mt-5 flex items-center justify-between gap-3">
          <span className="text-[11px] text-slate-300">
            {recipients === 'students' && 'Delivers to student inboxes'}
            {recipients === 'parents' && 'Delivers to linked parents only'}
            {recipients === 'both' && 'Delivers to students AND their linked parents'}
          </span>
          <button onClick={send} disabled={sending || !message.trim()} data-testid="nudge-send"
            className="inline-flex items-center gap-2 rounded-full border border-sky-300/40 bg-sky-400/[0.16]
              px-5 py-2.5 text-sm font-semibold text-sky-100 hover:bg-sky-400/[0.26] disabled:opacity-40 transition-colors">
            <Send className="h-3.5 w-3.5" /> {sending ? 'Sending…' : `Send${recipients==='both'?' to all':''}`}
          </button>
        </div>
      </div>
    </div>
  );
}
